/**
 * Page Notre Gouvernance - FTPE Outremer
 */
import React from 'react';
import { Users, Vote, Eye, Scale, Building2, UserCheck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

// Logo FTPE Outremer
const FTPE_LOGO = "https://customer-assets.emergentagent.com/job_80d9c916-7281-4428-9c84-a6e0cd256c92/artifacts/ojac2aim_logo%20ftpe%20outremer%20dans%20fond%20blanc.png";

export default function FTPEGouvernancePage() {
  const navigate = useNavigate();

  const instances = [
    {
      icon: Users,
      title: "Assemblée Générale",
      description: "Instance souveraine réunissant l'ensemble des adhérents. Elle vote les orientations, le budget et élit les administrateurs."
    },
    {
      icon: Building2,
      title: "Conseil d'Administration",
      description: "Organe de décision et de contrôle. Il définit la stratégie et veille à la bonne gestion du syndicat."
    },
    {
      icon: UserCheck,
      title: "Bureau exécutif",
      description: "Assure la gestion courante et met en œuvre les décisions du Conseil d'Administration."
    }
  ];

  const principes = [
    { icon: Vote, text: "Démocratie interne : un adhérent = une voix" },
    { icon: Eye, text: "Transparence : comptes et décisions accessibles" },
    { icon: Scale, text: "Responsabilité : mandats clairs et limités" }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 text-white py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <BackButton variant="dark" label="Retour" />
          </div>
          
          <div className="flex items-center gap-6 mb-8">
            <img src={FTPE_LOGO} alt="FTPE Outremer" className="w-20 h-20 object-contain rounded-lg bg-white p-2" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">
                Notre <span className="text-amber-400">gouvernance</span>
              </h1>
              <p className="text-xl text-slate-300">Démocratie, transparence et responsabilité</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-5xl mx-auto bg-white shadow-lg -mt-10 relative z-10 rounded-2xl p-8 md:p-12 mb-12">
        
        {/* Principes */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-700 mb-6">Nos principes de gouvernance</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {principes.map((principe, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                <principe.icon className="w-6 h-6 text-green-700 flex-shrink-0" />
                <p className="text-sm text-slate-700 font-medium">{principe.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Instances */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-700 mb-6">Les instances</h2>
          <div className="space-y-6">
            {instances.map((instance, index) => (
              <div key={index} className="flex items-start gap-6 p-6 bg-slate-50 rounded-xl border-l-4 border-amber-500">
                <div className="p-4 bg-green-100 rounded-xl flex-shrink-0">
                  <instance.icon className="w-8 h-8 text-green-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{instance.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{instance.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Engagement */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-green-700 mb-4">Notre engagement</h2>
          <div className="bg-gradient-to-r from-green-50 to-amber-50 p-6 rounded-xl border border-green-200">
            <p className="text-slate-700 leading-relaxed">
              La FTPE Outremer s'engage à respecter les plus hauts standards de gouvernance :
              publication des comptes, rapports d'activité accessibles, élections régulières
              et ouvertes à tous les adhérents.
            </p>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/adhesion-ftpe-workflow')}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 text-lg rounded-xl"
          >
            Participer à la vie du syndicat
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 text-center">
        <p className="font-semibold">FTPE Outremer – Syndicat patronal ultramarin</p>
        <p className="text-slate-400 text-sm mt-2">Démocratie • Transparence • Responsabilité</p>
      </footer>
    </div>
  );
}
